<template>
  <div
      class="container-fluid files-list-overflow"
      style="max-width: 1300px;"
  >
  <div class="card w-100">
    <div class="card-header view">
      <div class="header">
        <div class="title">
          <div>Upload Studies</div>
        </div>
        <div class="steps">
          <el-steps :active="0" finish-status="success">
            <el-step title="Step 1"></el-step>
            <el-step title="Step 2"></el-step>
          </el-steps>
        </div>
      </div>
    </div>
    <div class="card-body">
      <div class="form-wrapper">
        <div class="w-50 ml-auto mr-auto p-4">
          <div class="w-100 test-study-hint">
            Test data:
            <a href="/test-study-template/Template_Study.zip" download>Template study (zip)</a>
            &middot;
            <a href="#" @click.prevent="showTemplateHelp = true">Format reference</a>
          </div>

          <el-dialog
            v-model="showTemplateHelp"
            title="Building a test study"
            width="650px"
          >
            <div class="template-help-body">
              <p>
                A minimal valid study needs exactly two files, zipped with this
                folder shape:
              </p>
              <pre class="template-help-code">&lt;Client&gt;/&lt;Study Name&gt;/Study Material/&lt;Study Name&gt;_Arc Enabler.xlsm
&lt;Client&gt;/&lt;Study Name&gt;/Results/&lt;Study Name&gt;_RDF.xlsx</pre>
              <p>
                Everything else (Brief, Protocol, Questionnaire, Screener, Data
                Tabs) is optional padding &mdash; the pipeline only warns on a
                missing file, it doesn't fail, as long as those two are present
                and correctly shaped.
              </p>

              <h4>Arc Enabler (the request form)</h4>
              <p>
                Filename must match <code>arc.*enabler.*\.xls[m|x]$</code>
                (case-insensitive). Needs both a <code>Study Details</code> and
                a <code>Fragrance Details</code> sheet. <code>Study Details</code>
                is key/value, one row per field &mdash; the field that matters
                most for integration testing is <strong>Pace-Number</strong>.
              </p>

              <h4>RDF results file</h4>
              <p>
                Filename must match <code>rdf.*\.(xls|csv)</code>. Needs a
                <code>DATA MAP</code> sheet with <strong>exactly 4 columns</strong>
                (<code>q_id</code>, <code>q_text</code>, <code>q_attribute</code>,
                <code>q_options</code>) and a <code>DATA</code> sheet with
                matching column headers. A sheet with a different column count
                fails with "Expected 4 columns in the datamap".
              </p>

              <p class="template-help-footer">
                Full reference with examples:
                <a
                  href="https://github.com/SymriseAG/harbour-app-iris/blob/main/backend/docs/TEST_STUDY_TEMPLATE.md"
                  target="_blank"
                  rel="noopener noreferrer"
                >TEST_STUDY_TEMPLATE.md</a>
              </p>
            </div>

            <template #footer>
              <el-button @click="showTemplateHelp = false">Close</el-button>
            </template>
          </el-dialog>
          <div class="w-100 d-flex">
            <div class="col-div">
              <div class="label-div">
                <label>Client</label>
              </div>
              <el-select
                v-model="localFormData.client"
                class="type-input w-100"
                placeholder="Select client"
                filterable
                remote
                reserve-keyword
                :popper-append-to-body="true"
                ref="clientSelect"
                @change="closeSelect('clientSelect')"
                :remote-method="filterClients"
                @visible-change="resetClientOptions"
              >
                <el-option-group label="Press Tab/Escape to exit">
                  <el-option
                    v-for="client in filteredClients"
                    :key="client.value" :label="client.value" :value="client.value"
                  >
                  </el-option>
                </el-option-group>
              </el-select>
            </div>
          </div>
          <div class="w-100 d-flex">
            <div class="col-div">
              <div class="label-div">
                <label>Region</label>
              </div>
              <el-select
                class="type-input w-100"
                v-model="localFormData.region"
                placeholder="Select region"
                filterable
                remote
                reserve-keyword
                :popper-append-to-body="true"
                ref="regionSelect"
                @change="closeSelect('regionSelect')"
                :remote-method="filterRegions"
                @visible-change="resetRegionOptions"
              >
                <el-option-group label="Press Tab/Escape to exit">
                  <el-option
                    v-for="region in filteredRegions"
                    :key="region.value" :label="region.value" :value="region.value"
                  >
                  </el-option>
                </el-option-group>
              </el-select>
            </div>
            <div class="col-div">
              <div class="label-div">
                <label>Year</label>
              </div>
              <el-select
                class="type-input w-100"
                v-model="localFormData.year"
                placeholder="Select year"
                filterable
                remote
                reserve-keyword
                :popper-append-to-body="true"
                ref="yearSelect"
                @change="closeSelect('yearSelect')"
                :remote-method="filterYears"
                @visible-change="resetYearOptions"
              >
                <el-option-group label="Press Tab/Escape to exit">
                  <el-option
                    v-for="year in filteredYears"
                    :key="year" :label="year" :value="year"
                  />
                </el-option-group>
              </el-select>
            </div>
          </div>
          <div class="w-100 file-input custom-padding">
            <div class="w-100 text-center">
              <el-upload
                class="upload-file el-upload-input-custom-styles"
                :ref="`upload`"
                :show-file-list="false"
                :on-change="(f, flist) => handleChange(f)"
                :auto-upload="false"
              >
                <el-input
                  :value="fileNames"
                  class="el-input-upload"
                  placeholder="Select or drop zip file"
                  clearable
                  readonly
                />
              </el-upload>
            </div>
          </div>
          <div class="w-100 text-center">
            <button
              v-if="fileList.length"
              class="clear-files-btn mr-1"
              @click="handleClearFiles"
            >
              Clear files
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer">
      <div class="d-flex justify-content-end">
        <button
          class="btn next-button"
          @click="handleSubmitFiles"
          :disabled="!fileList.length"
        >
          Next
        </button>
      </div>
      <div class="w-100 mt-2">
        <StudiesUploadHistory
          :table-data="fileProcessList"
          :go-to-file-process-details="goToFileProcessDetails"
        />
      </div>
    </div>
  </div>
    </div>
</template>

<script>
import StudiesUploadHistory
  from '@/components/StudiesUpload/StudiesUploadHistory'
import _ from 'lodash'

export default {
  name: 'StudiesUploadFirstStep',
  emits: ['update:formData'],
  components: {
    StudiesUploadHistory
  },
  props: {
    'fileNames': String,
    'status': Object,
    'handleChange': Function,
    'handleSuccess': Function,
    'handleClearFiles': Function,
    'handleSubmitFiles': Function,
    'fileList': Array,
    'yearsToPick': Array,
    'clientsToPick': Array,
    'regionsToPick': Array,
    'formData': Object,
    fileProcessList: Array,
    goToFileProcessDetails: Function
  },
  data () {
    return {
      localFormData: _.cloneDeep(this.formData),
      filteredClients: [],
      filteredRegions: [],
      filteredYears: [],
      showTemplateHelp: false
    }
  },
  watch: {
    formData: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localFormData)) {
          this.localFormData = _.cloneDeep(value)
        }
      }
    },
    clientsToPick: {
      immediate: true,
      handler (value) {
        this.filteredClients = Array.isArray(value) ? value.slice(0, 100) : []
      }
    },
    regionsToPick: {
      immediate: true,
      handler (value) {
        this.filteredRegions = Array.isArray(value) ? value.slice(0, 100) : []
      }
    },
    yearsToPick: {
      immediate: true,
      handler (value) {
        this.filteredYears = Array.isArray(value) ? value.slice(0, 20) : []
      }
    },
    localFormData: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.formData)) {
          this.$emit('update:formData', _.cloneDeep(value))
        }
      }
    }
  },
  methods: {
    resetClientOptions (isOpen) {
      if (!isOpen) return
      this.filteredClients = Array.isArray(this.clientsToPick) ? this.clientsToPick.slice(0, 100) : []
    },
    resetRegionOptions (isOpen) {
      if (!isOpen) return
      this.filteredRegions = Array.isArray(this.regionsToPick) ? this.regionsToPick.slice(0, 100) : []
    },
    resetYearOptions (isOpen) {
      if (!isOpen) return
      this.filteredYears = Array.isArray(this.yearsToPick) ? this.yearsToPick.slice(0, 20) : []
    },
    filterClients (query) {
      if (!Array.isArray(this.clientsToPick)) {
        this.filteredClients = []
        return
      }
      const q = String(query || '').trim().toLowerCase()
      if (!q) {
        this.filteredClients = this.clientsToPick.slice(0, 100)
        return
      }
      this.filteredClients = this.clientsToPick
        .filter(item => String(item.value || '').toLowerCase().includes(q))
        .slice(0, 100)
    },
    filterRegions (query) {
      if (!Array.isArray(this.regionsToPick)) {
        this.filteredRegions = []
        return
      }
      const q = String(query || '').trim().toLowerCase()
      if (!q) {
        this.filteredRegions = this.regionsToPick.slice(0, 100)
        return
      }
      this.filteredRegions = this.regionsToPick
        .filter(item => String(item.value || '').toLowerCase().includes(q))
        .slice(0, 100)
    },
    filterYears (query) {
      if (!Array.isArray(this.yearsToPick)) {
        this.filteredYears = []
        return
      }
      const q = String(query || '').trim().toLowerCase()
      if (!q) {
        this.filteredYears = this.yearsToPick.slice(0, 20)
        return
      }
      this.filteredYears = this.yearsToPick
        .filter(item => String(item).toLowerCase().includes(q))
        .slice(0, 20)
    },
    closeSelect (refName) {
      const select = this.$refs[refName]
      if (!select) return
      this.$nextTick(() => {
        if (typeof select.handleClose === 'function') {
          select.handleClose()
        }
        if (Object.prototype.hasOwnProperty.call(select, 'visible')) {
          select.visible = false
        }
        if (typeof select.blur === 'function') {
          select.blur()
        }
      })
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/scss/variables';
.el-upload-input-custom-styles {
  .el-upload {
    width: 100%;
    .el-input-upload {
      input.el-input__inner {
        pointer-events: none;
        height: 48px;
      }
      div.el-input-group__append {
        width: 100px;

        .el-input-upload-icon-div {
          width: 100%;
          padding: 0;

          button.el-button {
            padding: 10px 0 !important;
            width: 100%;

            span {
              svg {
                width: 100%;
                padding: 5px 0;
                margin: 0 !important;
                border-radius: 0.2rem;
              }
            }
          }
        }
      }
    }
  }
}

</style>
<style lang="scss" scoped>
@import '../../assets/scss/variables';
.custom-padding {
  padding: 2rem 1rem !important;
}

.test-study-hint {
  font-size: 14px;
  line-height: 1.4;
  color: $dusty-blue;
  text-align: center;
  padding: 0 1rem;

  a {
    color: $blueberry;
    text-decoration: underline;
  }
}

.template-help-body {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.template-help-code {
  background: #f5f6f8;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 13px;
  font-family: 'Courier New', monospace;
  overflow-x: auto;
  white-space: pre;
}

.template-help-footer {
  margin-top: 1.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e4e7ed;
  font-size: 13px;
}

.files-list-overflow {
  overflow-x: hidden;
  overflow-y: scroll;
}
.next-button {
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  background-color: $blueberry;
  color: white;
  text-transform: uppercase;
  border-radius: 4px;
  width: 160px;
  height: 38px;
  padding: 5px;
  @media screen and (min-width: $break-lg) {
    width: 180px;
    height: 40px;
    padding: 2px;
  }
  @media screen and (min-width: $break-xl) {
    width: 200px;
    height: 42px
  }
  @media screen and (min-width: $break-xxl) {
    width: 240px;
    height: 44px
  }
  transition: background-color 0.2s linear;
  &:hover {
    background-color: #9256D9;
  }
}

div.col-div {
  padding: 1rem;
  width: 55%;

  div.label-div {
    width: 100%;

    label {
      font-size: 22px;
    }
  }
}

.file-input {
  padding: 2rem;
  text-align: center;

  ::v-deep .upload-file {
    width: 100%;

  .el-input__inner {
    border-right: 0 !important;
  }

  .el-input-group__append {
    background-color: rgb(59, 134, 255) !important;
    cursor: pointer;
    padding: 0 !important;
    border: 0 !important;

    div {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      padding: 1px 8px;
      height: 100%;

      button {
        padding: 10px !important;
      }
    }
  }
}
}

.clear-files-btn, .send-files-btn {
  border: none;
  font-size: 20px;
  background: transparent;
  padding: .25rem .5rem;
  transition: .5s;
  border-radius: 10px;
}
.clear-files-btn:hover {
  background: $red-700;
  color: $soft-blue;
}
.send-files-btn:hover {
  background: $blue-400;
  color: $soft-blue;
}

.el-input-upload-icon {
  font-size: 38px;
  margin: 0 5px;
}

.card {
  margin-bottom: 0;
  border: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.93);
  max-width: 1300px;
}

.view {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  padding-bottom: 1rem;

  .header {
    display: flex;
    justify-content: space-between;

    .steps {
      width: 270px;
      height: 62px;
      margin-top: 20px;
      margin-right: 29px;
    }
  }

  .form-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 33px;
    @media screen and (min-width: $break-lg) {
      font-size: 20px;
      line-height: 38px;
    }
    @media screen and (min-width: $break-xl) {
      font-size: 24px;
      line-height: 43px;
    }
    @media screen and (min-width: $break-xxl) {
      font-size: 28px;
      line-height: 48px;
    }
    letter-spacing: 0;
    color: $dusty-blue;
    margin: 20px 0;
    @media screen and (min-width: $break-lg) {
      margin: 30px 0
    }
    @media screen and (min-width: $break-xl) {
      margin: 45px 0
    }
    @media screen and (min-width: $break-xxl) {
      margin: 60px 0
    }

    .type-input {
      margin-left: 20px;
      height: 43px !important;
      @media screen and (min-width: $break-lg) {
        height: 47px;
      }
      @media screen and (min-width: $break-xl) {
        height: 51px;
      }
      @media screen and (min-width: $break-xxl) {
        height: 55px;
      }
    }
  }
}

.title {
  margin-top: 20px;
  margin-left: 32px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 900;
  font-size: 30px;
  line-height: 24px;
  letter-spacing: 0.8px;
  @media screen and (min-width: $break-lg) {
    font-size: 35px;
    line-height: 26px;
    letter-spacing: 0.8px;
    margin-top: 23px;
    margin-left: 37px;
  }
  @media screen and (min-width: $break-xl) {
    font-size: 40px;
    line-height: 28px;
    letter-spacing: 0.9px;
    margin-top: 26px;
    margin-left: 42px;
  }
  @media screen and (min-width: $break-xxl) {
    font-size: 45px;
    line-height: 30px;
    letter-spacing: 0.9px;
    margin-top: 29px;
    margin-left: 47px;
  }
  color: $pewter;
  text-transform: uppercase;
}

.title svg {
  margin-right: 0.5em;
}

.upload-file {
  .el-input__inner {
    border-right: 0 !important;
  }

  .el-input-group__append {
    color: white !important;
    cursor: pointer;
    padding: 0 !important;
    border: 0 !important;

    div {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      padding: 1px 8px;
      height: 100%;

      button {
        padding: 10px !important;
      }
    }
  }
}
</style>
